import { apiSlice } from "../apiSlice";

export interface GetCustomerResponse {
  isAnonymousUser: boolean;
  id: string;
  userId: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  phoneNumber: string;
  email: string;
  photoUrl?: string;
  birthDate?: string;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  updatedAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  settings: {
    requiresOrderVerification: boolean;
  };
  addresses: {
    id: string;
    tag: string;
    address: string;
    location?: {
      latitude?: number;
      longitude?: number;
    };
    isSelected: boolean;
  }[];
}

interface UpdateProfileRequest {
  userId: string;
  customerId: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  phoneNumber?: string;
  birthDate?: string;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  updatedAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  settings?: {
    requiresOrderVerification: boolean;
  };
  addresses: {
    id: string;
    tag: string;
    address: string;
    location?: {
      latitude: number;
      longitude: number;
    };
    isSelected: boolean;
  }[];
}

interface walletBalanceResponse {
  walletBalance: number;
}

interface CustomerOrganizationDetailsResponse {
  organizationId: string;
  organizationName: string;
  deliveryPoint: {
    deliveryAddress: string;
    deliveryLatitude: number;
    deliveryLongitude: number;
    deliveryContactName: string;
    deliveryContactPhone: string;
  } | null;
  creditSettings?: {
    creditTermDays: number;
    memberDefaultLimit: number;
    allowCreditOrders: boolean;
  }
  supportsDeliveryPoint?: boolean;
  organizationWallet?: {
    currentOutstanding: number;
    creditLimit: number;
    balance: number;
  },
  wallet: {
    currentOutstanding: number;
    creditLimit: number;
    balance: number;
  };
}

export interface SavedCard {
  index: number;
  cardType: string;
  cardNumberLast4: string;
  cardExpiryDate: string;
  isDefault: boolean;
  paymentToken?: string;
  createdAt?: {
    _seconds: number;
    _nanoseconds: number;
  };
}

export interface SavedCardsResponse {
  success: boolean;
  paymentMethods: SavedCard[];
  count: number;
}

const API_BASE_DOMAIN = process.env.REACT_APP_API_BASE_DOMAIN_PRODUCTION;

const PROFILE_IMAGE_UPDATE_URL = process.env.REACT_APP_PROFILE_IMAGE_UPDATE_URL;
export const CustomerService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCustomerProfile: builder.query<GetCustomerResponse, string>({
      query: (accessToken) => ({
        url: `https://customersitehttps-getcustomerprofile-${API_BASE_DOMAIN}-uc.a.run.app`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }),
      keepUnusedDataFor: 300,
    }),

    updateCustomerProfile: builder.mutation<string, UpdateProfileRequest>({
      query: (profileData) => ({
        url: `https://customersitehttps-updateprofile-${API_BASE_DOMAIN}-uc.a.run.app`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: profileData,
      }),
    }),

    getProfileImageUploadUrl: builder.query<{ signedUrl: string }, string>({
      query: (fileExtension) =>
        `https://customersitehttps-getprofileimageuploadurl-${API_BASE_DOMAIN}-uc.a.run.app?fileExtension=${fileExtension}`,
    }),

    updateProfileImage: builder.mutation<void, { userId: string; fileExtension: string }>({
      query: ({ userId, fileExtension }) => ({
        url: `https://customersitehttps-updateprofileimage-${API_BASE_DOMAIN}-uc.a.run.app`,
        method: "POST",
        body: {
          photoUrl: `${PROFILE_IMAGE_UPDATE_URL}/Customers/${userId}/profile-image.${fileExtension}`,
        },
      }),
    }),

    getWalletBalance: builder.query<walletBalanceResponse, string>({
      query: (customerId) => ({
        url: `https://customerhttps-getcustomerwalletbalance-${API_BASE_DOMAIN}-uc.a.run.app?customerId=${customerId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 180,
    }),
    getCustomerOrganizationDetails: builder.query<CustomerOrganizationDetailsResponse[], string>({
      query: (customerId) => ({
        url: `https://customersitehttps-getorganizationsandwalletbycust-${API_BASE_DOMAIN}-uc.a.run.app?customerId=${customerId}`,
        method: "GET",
      }),
    }),
    getSavedCardsDetailsForUser: builder.query<SavedCardsResponse, string>({
      query: (userId) => ({
        url: `https://userhttps-getusersavedpaymentmethods-${API_BASE_DOMAIN}-uc.a.run.app?userId=${userId}`,
        method: "POST",
      }),
    }),
    updateUserPaymentMethods: builder.mutation<any, { userId: string; savedPaymentMethods: SavedCard[] }>({
      query: ({ userId, savedPaymentMethods }) => ({
        url: `https://customersitehttps-updateuserpaymentmethods-${API_BASE_DOMAIN}-uc.a.run.app`,
        method: "PUT",
        body: { userId, savedPaymentMethods },
      }),
    }),
  }),
});

export const {
  useGetCustomerProfileQuery,
  useLazyGetCustomerProfileQuery,
  useUpdateCustomerProfileMutation,
  useLazyGetProfileImageUploadUrlQuery,
  useUpdateProfileImageMutation,
  useGetWalletBalanceQuery,
  useLazyGetWalletBalanceQuery,
  useGetCustomerOrganizationDetailsQuery,
  useLazyGetCustomerOrganizationDetailsQuery,
  useGetSavedCardsDetailsForUserQuery,
  useLazyGetSavedCardsDetailsForUserQuery,
  useUpdateUserPaymentMethodsMutation,
} = CustomerService;
